import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useAuth } from '../../contexts/AuthContext';
import { isAdminPanelRole } from '../../constants/rbac';
import { usePageTitle } from '../../hooks/usePageTitle';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { extractError } from '../../utils';

const adminLoginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type AdminLoginFormData = z.infer<typeof adminLoginSchema>;

const AdminLoginPage: React.FC = () => {
  usePageTitle('Admin Sign In — Easy and Quiet Hostel');
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, touchedFields },
  } = useForm<AdminLoginFormData>({
    resolver: zodResolver(adminLoginSchema),
    mode: 'onTouched',
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const emailValue = watch('email');
  const forgotPasswordHref = emailValue?.trim()
    ? `/forgot-password?email=${encodeURIComponent(emailValue.trim())}`
    : '/forgot-password';

  const onSubmit = async (data: AdminLoginFormData) => {
    try {
      setIsLoading(true);
      setError(null);
      await login(data);

      const savedUser = localStorage.getItem('user');
      const role = savedUser ? JSON.parse(savedUser)?.role : null;

      if (!isAdminPanelRole(role)) {
        logout();
        setError('This account does not have admin dashboard access.');
        return;
      }

      navigate('/dashboard');
    } catch (err: any) {
      setError(extractError(err, 'Unable to sign in. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-overline uppercase text-muted-foreground">
            Admin Dashboard
          </h1>
          <h2 className="mt-3 text-center text-3xl font-extrabold text-foreground">
            Admin Sign In
          </h2>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            Access facilities, balloting controls, and payment operations.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <Input
              id="admin-email"
              label="Email address"
              type="email"
              autoComplete="email"
              {...register('email')}
              error={touchedFields.email ? errors.email?.message : undefined}
            />
            <Input
              id="admin-password"
              label="Password"
              type="password"
              autoComplete="current-password"
              {...register('password')}
              error={touchedFields.password ? errors.password?.message : undefined}
            />
            <div className="text-right">
              <Link
                to={forgotPasswordHref}
                className="text-xs font-medium text-brand hover:text-brand-light"
              >
                Forgot password?
              </Link>
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-destructive-subtle px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
              {error}
            </div>
          )}

          <Button type="submit" loading={isLoading} className="w-full" size="lg">
            Sign in
          </Button>

          <div className="text-center text-sm text-muted-foreground">
            <Link to="/admin" className="font-medium text-brand hover:text-brand-light">
              Back to admin access page
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminLoginPage;
